import { DeleteOutlined, EditOutlined, MailOutlined, SendOutlined } from '@ant-design/icons'
import {
	Button,
	Input,
	Popconfirm,
	Select,
	Space,
	Table,
	Tag,
	message,
} from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import api from '../../api/index.api'
import { ListPageIntro, ListPageShell } from '../../common'
import { ROUTE } from '../../utils/constant'
import './EmailContentList.scss'

const STATUS_COLORS = {
	draft: 'default',
	active: 'success',
	archived: 'warning',
}

const TYPE_COLORS = {
	transactional: 'blue',
	marketing: 'purple',
	newsletter: 'cyan',
	other: 'geekblue',
}

export const EmailContentList = () => {
	const history = useHistory()
	const [items, setItems] = useState([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [page, setPage] = useState(0)
	const [pageSize, setPageSize] = useState(20)
	const [status, setStatus] = useState(undefined)
	const [emailType, setEmailType] = useState(undefined)
	const [query, setQuery] = useState('')

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const rs = await api.emailAdminApi.getTemplates({
				page,
				pageSize,
				status,
				emailType,
				q: query.trim() || undefined,
			})
			setItems(Array.isArray(rs?.data) ? rs.data : [])
			setTotal(Number(rs?.total) || 0)
		} catch (err) {
			message.error(err?.message || 'Could not load email templates.')
		} finally {
			setLoading(false)
		}
	}, [page, pageSize, status, emailType, query])

	useEffect(() => {
		load()
	}, [load])

	const handleDelete = async id => {
		try {
			await api.emailAdminApi.deleteTemplate(id)
			message.success('Template deleted.')
			load()
		} catch (err) {
			message.error(err?.message || 'Could not delete template.')
		}
	}

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			ellipsis: true,
		},
		{
			title: 'Slug',
			dataIndex: 'slug',
			key: 'slug',
			ellipsis: true,
		},
		{
			title: 'Type',
			dataIndex: 'emailType',
			key: 'emailType',
			render: value => (
				<Tag color={TYPE_COLORS[value] || 'default'}>{value}</Tag>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: value => (
				<Tag color={STATUS_COLORS[value] || 'default'}>{value}</Tag>
			),
		},
		{
			title: 'Subject',
			dataIndex: 'subject',
			key: 'subject',
			ellipsis: true,
		},
		{
			title: 'Updated',
			dataIndex: 'updatedAt',
			key: 'updatedAt',
			width: 180,
			render: value => (value ? new Date(value).toLocaleString() : '—'),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 200,
			render: (_, record) => (
				<Space size={4}>
					<Button
						size="small"
						icon={<EditOutlined />}
						onClick={() =>
							history.push(
								`${ROUTE.EMAIL_CONTENT_MANAGEMENT}${ROUTE.CREATE}?id=${record.id}`
							)
						}
					>
						Edit
					</Button>
					<Button
						size="small"
						icon={<SendOutlined />}
						disabled={record.status !== 'active'}
						onClick={() =>
							history.push(
								`${ROUTE.EMAIL_CONTENT_MANAGEMENT}${ROUTE.EMAIL_SEND}?templateId=${record.id}`
							)
						}
					>
						Send
					</Button>
					<Popconfirm
						title="Delete this template?"
						onConfirm={() => handleDelete(record.id)}
					>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	]

	return (
		<ListPageShell className="email-content-list">
			<ListPageIntro
				icon={MailOutlined}
				title="Email templates"
				subtitle="Reusable templates for transactional, marketing, and newsletter emails."
			/>
			<div className="email-content-list__toolbar">
				<Space wrap>
					<Input.Search
						allowClear
						placeholder="Search name, slug, subject"
						onSearch={value => {
							setPage(0)
							setQuery(value || '')
						}}
						style={{ width: 260 }}
					/>
					<Select
						allowClear
						placeholder="Status"
						style={{ width: 140 }}
						value={status}
						onChange={value => {
							setPage(0)
							setStatus(value)
						}}
						options={[
							{ value: 'draft', label: 'Draft' },
							{ value: 'active', label: 'Active' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>
					<Select
						allowClear
						placeholder="Type"
						style={{ width: 160 }}
						value={emailType}
						onChange={value => {
							setPage(0)
							setEmailType(value)
						}}
						options={[
							{ value: 'transactional', label: 'Transactional' },
							{ value: 'marketing', label: 'Marketing' },
							{ value: 'newsletter', label: 'Newsletter' },
							{ value: 'other', label: 'Other' },
						]}
					/>
					<Button
						type="primary"
						onClick={() =>
							history.push(
								`${ROUTE.EMAIL_CONTENT_MANAGEMENT}${ROUTE.CREATE}`
							)
						}
					>
						Create template
					</Button>
				</Space>
			</div>
			<Table
				rowKey="id"
				loading={loading}
				columns={columns}
				dataSource={items}
				pagination={{
					current: page + 1,
					pageSize,
					total,
					showSizeChanger: true,
					onChange: (nextPage, nextSize) => {
						setPage(nextPage - 1)
						setPageSize(nextSize)
					},
				}}
			/>
		</ListPageShell>
	)
}
